/* eslint import/no-unresolved: 0 */
import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "components/Header";
import Footer from "components/Footer";

// APIベース
const API_BASE: string =
  (process.env.REACT_APP_API_BASE as string) ||
  ((import.meta as any)?.env?.VITE_API_BASE as string) ||
  "http://localhost:4000";
const API_PUBLIC = `${API_BASE}/public`;

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ReviewNew() {
  const nav = useNavigate();
  const params = useParams();
  const q = useQuery();

  const planIdParam = params.planId || "";
  const [planId, setPlanId] = useState<string>(planIdParam);
  const [planName, setPlanName] = useState<string>(q.get("planName") || "");

  const [rating, setRating] = useState<number>(5);
  const [comment, setComment] = useState<string>("");

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const maxWidthStyle: React.CSSProperties = useMemo(
    () => ({ maxWidth: 720, width: "100%", margin: "0 auto", padding: "16px 0 64px" }),
    []
  );
  const panel: React.CSSProperties = {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
    padding: 16,
  };
  const faded: React.CSSProperties = { opacity: 0.6 };

  const canSubmit =
    !submitting &&
    (planId || planName) &&
    rating >= 1 &&
    rating <= 5 &&
    comment.trim().length >= 5;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSubmitting(true);
      setErr(null);

      const payload: any = {
        planId: planId || undefined,
        planName: planName || undefined,
        rating,
        comment: comment.trim(),
      };

      const res = await fetch(`${API_PUBLIC}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDone(true);

      // 投稿後は一覧へ
      setTimeout(() => nav("/reviews", { replace: true }), 800);
    } catch (e: any) {
      setErr(e?.message || "送信に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />

      <main className="bgScope lp" style={{ background: "transparent" }}>
        <div className="stack" style={maxWidthStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 8, marginBottom: 12 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>レビューを書く</h1>
            <Link to="/mypage" style={{ textDecoration: "none", fontSize: 13 }}>← マイページへ戻る</Link>
          </div>

          <section style={panel}>
            <form onSubmit={onSubmit}>
              {/* プラン指定 */}
              <div style={{ display: "grid", gap: 10 }}>
                <label style={{ fontSize: 13 }}>
                  プランID（自動入力／任意）
                  <input
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    placeholder="例: 123"
                    style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #ddd", marginTop: 6 }}
                  />
                </label>

                <label style={{ fontSize: 13 }}>
                  プラン名（表示用／任意）
                  <input
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    placeholder="例: ゴールドプラン"
                    style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #ddd", marginTop: 6 }}
                  />
                </label>
              </div>

              {/* レーティング */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13, ...faded, marginBottom: 6 }}>評価（1〜5）</div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      aria-label={`rating-${n}`}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 999,
                        border: "1px solid",
                        borderColor: n === rating ? "#111" : "#ddd",
                        background: "#fff",
                        fontWeight: n === rating ? 700 : 400,
                        cursor: "pointer",
                      }}
                    >
                      {"★".repeat(n)}
                    </button>
                  ))}
                </div>
              </div>

              {/* コメント */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 13, ...faded, marginBottom: 6 }}>コメント（5文字以上）</div>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={5}
                  placeholder="例：かなり参考になりました！"
                  style={{
                    width: "100%",
                    padding: 12,
                    borderRadius: 12,
                    border: "1px solid #ddd",
                    resize: "vertical",
                  }}
                />
              </div>

              {/* エラー・完了 */}
              {err && <p style={{ color: "crimson", marginTop: 10 }}>{err}</p>}
              {done && <p style={{ color: "teal", marginTop: 10 }}>投稿しました。まもなく一覧に戻ります…</p>}

              {/* 送信 */}
              <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 12,
                    border: "1px solid #111",
                    background: "#111",
                    color: "#fff",
                    cursor: canSubmit ? "pointer" : "not-allowed",
                    opacity: canSubmit ? 1 : 0.5,
                  }}
                >
                  {submitting ? "送信中…" : "レビューを投稿する"}
                </button>
                <Link to="/reviews" style={{ padding: "10px 16px" }}>一覧へ戻る</Link>
              </div>

              <p style={{ fontSize: 12, marginTop: 8, ...faded }}>
                ※ 購入者レビューのみ掲載対象です。内容により非公開となる場合があります。
              </p>
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}
